import { styled } from '@linaria/react';
import { Pagination, ScrollArea, Stack, Text } from '@mantine/core';
import { useMemo, useState } from 'react';
import { useRiskInsights } from '../../api/insights-client/insights-client.hooks';
import type { InsightFilters } from '../../api/insights-client/insights-client.type';
import { InsightType } from '../../api/insights-client/insights-client.type';
import { inkLight } from '../../styles/design-tokens';
import { Icon } from '../../ui-library/icon/icon';
import { InsightCard } from './insight-card';

const ITEMS_PER_PAGE = 10;

type FloraInsightsViewProps = {
  insightType?: InsightType;
  onViewAffectedItems?: (insightId: string) => void;
  insightTypeFilters?: string[];
  severityFilters?: string[];
  statusFilters?: string[];
  viewFilter?: string;
};

export function FloraInsightsView({
  insightType,
  onViewAffectedItems,
  insightTypeFilters = [],
  severityFilters = [],
  statusFilters = [],
  viewFilter = 'active',
}: FloraInsightsViewProps) {
  const [currentPage, setCurrentPage] = useState(1);

  // Convert sidebar filter state to InsightFilters format
  // Only apply filters if not all options are selected
  // This ensures we don't over-filter when user wants to see all
  const allInsightTypes = ['task', 'epic', 'initiative', 'pull_request'];
  const allSeverities = ['high', 'medium', 'low'];
  const allStatuses = ['not_started', 'in_progress', 'completed'];

  const shouldFilterByInsightType = insightTypeFilters.length > 0 && insightTypeFilters.length < allInsightTypes.length;
  const shouldFilterBySeverity = severityFilters.length > 0 && severityFilters.length < allSeverities.length;
  const shouldFilterByStatus = statusFilters.length > 0 && statusFilters.length < allStatuses.length;

  // Use insightType prop if no filters provided, otherwise use filtered types
  const effectiveInsightType = insightTypeFilters.length === 0 ? insightType : undefined;

  const filters: InsightFilters = {
    insight_type: effectiveInsightType,
    insight_types: shouldFilterByInsightType ? (insightTypeFilters as any[]) : undefined,
    severity: shouldFilterBySeverity ? (severityFilters[0] as any) : undefined,
    status: shouldFilterByStatus ? (statusFilters[0] as any) : undefined,
    is_active: viewFilter === 'active' || viewFilter === 'dismissed',
    is_dismissed: viewFilter === 'dismissed',
  };

  const { insights, query } = useRiskInsights(filters);

  // Calculate pagination
  const totalPages = Math.ceil(insights.length / ITEMS_PER_PAGE);
  const paginatedInsights = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return insights.slice(startIndex, endIndex);
  }, [insights, currentPage]);

  return (
    <Container>
      <ScrollArea h="calc(100vh - 240px)">
        <InsightsContainer>
          {query.isLoading ? (
            <EmptyState>
              <Icon name="hourglass_empty" color={inkLight} variant="filled" size={48} />
              <Text size="md" c={inkLight} fw={500}>
                Loading insights...
              </Text>
            </EmptyState>
          ) : insights.length === 0 ? (
            <EmptyState>
              <Icon name="lightbulb" color={inkLight} variant="filled" size={48} />
              <Text size="md" c={inkLight} fw={500}>
                No insights found
              </Text>
              <Text size="sm" c={inkLight}>
                Insights will appear here once Flora analyzes your work patterns
              </Text>
            </EmptyState>
          ) : (
            paginatedInsights.map((insight) => (
              <InsightCard key={insight.id} insight={insight} onViewAffectedItems={onViewAffectedItems} />
            ))
          )}
        </InsightsContainer>
      </ScrollArea>

      {!query.isLoading && insights.length > 0 && totalPages > 1 && (
        <PaginationContainer>
          <Pagination value={currentPage} onChange={setCurrentPage} total={totalPages} size="sm" />
        </PaginationContainer>
      )}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const InsightsContainer = styled(Stack)`
  gap: 16px;
  padding: 8px 0;
`;

const EmptyState = styled(Stack)`
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  gap: 12px;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 0;
  border-top: 1px solid #f3f4f6;
  margin-top: auto;
`;
